import { ANDROID_WRAPPER_EVENTS } from '@/event-manager/types';
import { useStore } from '@/store/useStore';
import { sendAndroidWrapperEvent } from '@/utilities/helpers/wrapperHelpers';

import {
  isDetachedMode,
  isEmbeddedInLobby,
  isEmbeddedQueryRequested,
} from '../embedded-mode';
import { getLobbyWindowForMessaging } from './web-wrapper-bridge';
import {
  notifyShellCloseRequest,
  notifyShellReloadRequired,
} from './web-wrapper-shell';

export const EMBEDDED_NAVIGATION = {
  EXTERNAL_FEATURES: 'noopener,noreferrer',
  EXTERNAL_TARGET: '_blank',
} as const;

export const isEmbeddedShell = (): boolean =>
  !isDetachedMode() && (isEmbeddedInLobby() || isEmbeddedQueryRequested());

export const closeLauncherShell = (): Promise<boolean> =>
  notifyShellCloseRequest();

export const requestLauncherReload = async (
  wrapperPayload?: unknown,
): Promise<void> => {
  const { wrapperType } = useStore.getState().runtime;
  if (wrapperType.isAndroid) {
    sendAndroidWrapperEvent(
      ANDROID_WRAPPER_EVENTS.RELOAD_GAME,
      wrapperPayload ?? '',
    );
    return;
  }

  if (isEmbeddedShell()) {
    const handled = await notifyShellReloadRequired();
    if (handled) {
      return;
    }
  }

  window.location.reload();
};

export const requestLauncherLogout = async (): Promise<void> => {
  window.location.reload();
};

/** Opens external content without navigating the embedded game-window iframe. */
export const openLauncherExternalUrl = (url?: string): void => {
  if (!url) {
    return;
  }

  const { wrapperType } = useStore.getState().runtime;
  if (wrapperType.isAndroid) {
    sendAndroidWrapperEvent(ANDROID_WRAPPER_EVENTS.OPEN_EXTERNAL_WEBVIEW, url);
    return;
  }

  if (isEmbeddedShell()) {
    window.open(
      url,
      EMBEDDED_NAVIGATION.EXTERNAL_TARGET,
      EMBEDDED_NAVIGATION.EXTERNAL_FEATURES,
    );
    return;
  }

  window.location.replace(url);
};

const resolveTargetOrigin = (targetOrigin?: string): string => {
  if (!targetOrigin) {
    return window.location.origin;
  }

  try {
    return new URL(targetOrigin).origin;
  } catch {
    return targetOrigin;
  }
};

export const postMessageToLobbyOpener = (
  message: unknown,
  targetOrigin?: string,
): void => {
  const lobbyWindow = getLobbyWindowForMessaging();
  if (!lobbyWindow) {
    return;
  }

  lobbyWindow.postMessage(message, resolveTargetOrigin(targetOrigin));
};
